import { useContext, useEffect, useState } from "react";

import { IColumn, ISortedBy, ITableRowData } from "../../../data-table/data-table";
import "./reference-widget.scss";
import Spinner from "../../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../../shared/context/auth-context";
import useHttp from "../../../../../shared/hooks/use-http";
import { postSingle } from "../../../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ControlledTable from "../../../controlled-table/controlled-table";
import ReferenceWidgetModal from "./reference-widget-modal/reference-widget-modal";

export interface IReferenceWidget {
  id: string;
  type: "reference";
  modalType: "field"
  label: string;
  required: boolean;
  readonly: boolean;
  reference_id: string;
  selectedColumns: IColumn[];
  modalData?: { // Extra information the modal needs to edit this widget
    [index: string]: string;
  }
}

interface IReferenceWidgetProps {
  widget: IReferenceWidget;
  selectedIds: string[] | undefined;
  previewMode?: boolean;
  selectMultiple?: boolean;
  onChange?(newValue: string[], key?: string): void;
}

const ReferenceWidget = ({widget, selectedIds: preSelectedIds, previewMode, selectMultiple, onChange}: IReferenceWidgetProps) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [data, setData] = useState<ITableRowData[]>([]);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({ column: undefined, direction: "asc" });
  const [initialized, setInitialized] = useState(false);

  const authCtx = useContext(AuthContext);

  const { sendRequest: getTableData, status: getTableDataStatus, data: getTableDataResult, error: getTableDataError } = useHttp(postSingle, true, authCtx.token, "datamodelTable", "platform");
  const { sendRequest: postEntityData, status: postEntityDataStatus, error: postEntityDataError, data: postEntityDataResult } = useHttp(
    postSingle,
    false,
    authCtx.token,
    "datamodelRowData",
    "platform"
  );

  useEffect(() => {
    if (!initialized && preSelectedIds && preSelectedIds.length > 0) {
      const isValid = !preSelectedIds.some(id => id === undefined);
      if (isValid) {
        setSelectedIds(preSelectedIds);
        setInitialized(true);
      }
    }
  }, [preSelectedIds]);

  useEffect(() => {
    if (previewMode) {
      const dummyData: ITableRowData[] = [];
      for (let i = 0; i < 2; i++) {
        dummyData.push(
          widget.selectedColumns.reduce((acc, curr) => {
            acc[curr.key] = "...";
            return acc;
          }, { id: i.toString() } as ITableRowData)
        );
      }
      setData(dummyData);
      return;
    }
    const orderByQuery = sortedByCol.column ? `?order_by=${sortedByCol.column} ${sortedByCol.direction}` : "";
    getTableData(`datamodel/table/${widget.reference_id}/entity${orderByQuery}`, {
      columns: widget.selectedColumns?.map(column => column.key) || [],
      rowData: []
    }, undefined, "none");
  }, [sortedByCol]);

  useEffect(() => {
    if (postEntityDataStatus === "completed" && !postEntityDataError && postEntityDataResult) {
      setShowModal(false);
      const orderByQuery = sortedByCol.column ? `?order_by=${sortedByCol.column} ${sortedByCol.direction}` : "";
      getTableData(`datamodel/table/${widget.reference_id}/entity${orderByQuery}`, {
        columns: widget.selectedColumns?.map(column => column.key) || [],
        rowData: []
      }, undefined, "none");

      setActiveId("");
      if (selectMultiple) {
        const newSelectedIds = [...selectedIds, postEntityDataResult.id];
        setSelectedIds(newSelectedIds);
        onChange?.(newSelectedIds, widget.id);
      } else {
        setSelectedIds([postEntityDataResult.id]);
        onChange?.([postEntityDataResult.id], widget.id);
      }
    }
  }, [postEntityDataStatus, postEntityDataResult, postEntityDataError]);

  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      setData(getTableDataResult.rows);
    }
  }, [getTableDataStatus, getTableDataResult, getTableDataError]);

  const handleOnReferenceChange = (selectedIds: string[]) => {
    setSelectedIds(selectedIds);
    onChange?.(selectedIds, widget.id);
  };

  const handleOnRowClick = (id: string) => {
    if (previewMode) {
      return;
    }
    setActiveId(id);
    onChange?.(selectedIds, widget.id);
    setShowModal(true);
  };

  const handleModalSubmit = (d: ITableRowData) => {
    if (previewMode) {
      return;
    }
    postEntityData("datamodel/record_import", {
      id: widget.reference_id,
      datamodel_type: "entity",
      row_data: JSON.stringify(d),
      rowData: []
    }, "none", "Entity created");
  };

  const handleOnNewClicked = () => {
    if (previewMode) {
      return;
    }
    setActiveId("");
    setShowModal(true);
  };

  const handleModalClosed = () => {
    if (previewMode) {
      return;
    }
    setShowModal(false);
    setActiveId("");
  };

  const renderReadonlyWidget = () => {
    const selectedData = data?.find((d) => d.id === selectedIds[0]);
    return <div className="referenceWidget-readonly">
      <h2>{widget.label}</h2>
      {widget.selectedColumns.map((column) => {
        if (column.dataType.type === "reference") {
          return <p key={column.key}>{column.displayName}: {selectedData?.[`${column.key}_name`]}</p>;
        }
        return <p key={column.key}>{column.displayName}: {selectedData?.[column.key]}</p>;
      })}
    </div>;
  };

  return <div className="referenceWidget">
    {validateLoadingStatusNew([getTableDataStatus, postEntityDataStatus]) && <Spinner />}
    {widget.readonly ? renderReadonlyWidget() :
      <div>
        <ReferenceWidgetModal
          entityId={widget.reference_id}
          selectedEntityId={activeId}
          label={widget.label}
          showModal={showModal}
          onModalSubmit={handleModalSubmit}
          onModalClosed={handleModalClosed}
          submitRecordStatus={postEntityDataStatus}
        />
        <ControlledTable
          title={`${widget.label}${widget.required ? " *" : ""}`}
          style="compact"
          data={data}
          selectionMode={selectMultiple ? "multi" : "single"}
          columns={widget.selectedColumns}
          onSorted={setSortedByCol}
          onNewClicked={handleOnNewClicked}
          onRowClick={handleOnRowClick}
          onScroll={noop}
          onSelectionChange={handleOnReferenceChange}
          preSelectedIds={selectedIds}
        />
      </div>
    }
  </div>;
};

export default ReferenceWidget;